import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useEffect } from 'react';
import { cn } from '@trajectory/ui';
import { type NotificationKind, useUiStore } from '@/stores/uiStore';

const KIND_ICON: Record<NotificationKind, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
};

const KIND_COLOR: Record<NotificationKind, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

function NotificationToast({ id, kind, title, message, durationMs }: {
  id: string;
  kind: NotificationKind;
  title: string;
  message?: string;
  durationMs: number;
}) {
  const dismiss = useUiStore((state) => state.dismiss);
  const Icon = KIND_ICON[kind];

  useEffect(() => {
    if (durationMs <= 0) return undefined;
    const timer = window.setTimeout(() => dismiss(id), durationMs);
    return () => window.clearTimeout(timer);
  }, [id, durationMs, dismiss]);

  return (
    <div className="flex w-80 items-start gap-2 rounded-panel border border-border bg-surface-raised p-3 shadow-lg">
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', KIND_COLOR[kind])} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text">{title}</p>
        {message && <p className="mt-0.5 text-xs text-muted">{message}</p>}
      </div>
      <button
        type="button"
        onClick={() => dismiss(id)}
        aria-label="Dismiss notification"
        className="text-faint hover:text-text"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Toast stack, anchored bottom-right, driven entirely by `uiStore.notifications`. */
export function NotificationCenter() {
  const notifications = useUiStore((state) => state.notifications);

  if (notifications.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
      {notifications.map((notification) => (
        <div key={notification.id} className="pointer-events-auto">
          <NotificationToast {...notification} />
        </div>
      ))}
    </div>
  );
}
